import { useEffect, useRef, useState } from 'react';
import { AVATAR_GROUPS } from '@srcbattle/shared';
import { Avatar, Banner, Button, isUploadedAvatar } from './ui.tsx';
import { ImageCropper } from './ImageCropper.tsx';

/**
 * Picking a picture: an emoji, or one of your own.
 *
 * The upload path is the interesting one, and almost all of its work happens on
 * the server (see `services/images.ts`) — the file is decoded and re-encoded
 * into a fresh WebP, so nothing the uploader put in the file survives the trip.
 * Nothing here should be mistaken for a security check; the accept attribute
 * and the size test below are courtesy, so somebody who picks a 30MB photo of
 * their dog gets told so instantly instead of after the upload.
 *
 * Groups rather than one long grid. Sixty emoji in a wall is not a choice, it
 * is a search problem, and people gave up and kept the ram.
 */

/** Matches the server's limit. Kept in sync by the message, which names it. */
const MAX_BYTES = 6 * 1024 * 1024;

export function AvatarPicker({
  value,
  busy,
  onPickEmoji,
  onPickFile,
  error,
}: {
  value: string;
  busy: boolean;
  onPickEmoji: (emoji: string) => void;
  onPickFile: (file: File) => void;
  error?: string | null;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [group, setGroup] = useState(AVATAR_GROUPS[0]?.label ?? '');
  const [localError, setLocalError] = useState<string | null>(null);
  const [cropFile, setCropFile] = useState<File | null>(null);

  const shown = AVATAR_GROUPS.find((candidate) => candidate.label === group) ?? AVATAR_GROUPS[0];

  function choose(file: File | undefined) {
    setLocalError(null);
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setLocalError(`That file is ${(file.size / 1024 / 1024).toFixed(1)}MB — the limit is 6MB.`);
      return;
    }
    setCropFile(file);
  }

  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const image = [...(event.clipboardData?.files ?? [])].find((file) =>
        file.type.startsWith('image/'),
      );
      if (!image) return;
      event.preventDefault();
      choose(image);
    };

    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []);

  return (
    <div className="flex flex-col gap-3">
      {cropFile && <ImageCropper file={cropFile} aspect={1} title="Frame your profile picture" onCancel={() => setCropFile(null)} onSave={(cropped) => { setCropFile(null); onPickFile(cropped); }} />}
      <div className="flex items-center gap-3">
        <Avatar value={value} size="lg" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">Your picture</p>
          <p className="text-xs text-text-faint">
            {isUploadedAvatar(value)
              ? 'Your own image. Paste another image here to replace it.'
              : 'Pick an emoji, upload an image, or paste one here.'}
          </p>
        </div>
        <Button variant="secondary" disabled={busy} onClick={() => fileRef.current?.click()}>
          Upload
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          className="hidden"
          aria-label="Upload a picture"
          onChange={(event) => {
            choose(event.target.files?.[0]);
            // Cleared so picking the same file twice in a row still fires.
            event.target.value = '';
          }}
        />
      </div>

      {(localError || error) && <Banner tone="danger">{localError ?? error}</Banner>}

      <div className="flex flex-wrap gap-1.5">
        {AVATAR_GROUPS.map((candidate) => (
          <button
            key={candidate.label}
            type="button"
            onClick={() => setGroup(candidate.label)}
            aria-pressed={candidate.label === shown?.label}
            className={`rounded-lg px-2.5 py-1 text-xs transition-colors ${
              candidate.label === shown?.label
                ? 'bg-accent text-accent-fg'
                : 'text-text-muted hover:text-text'
            }`}
          >
            {candidate.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-8 gap-1.5">
        {shown?.emoji.map((option) => (
          <button
            key={option}
            type="button"
            disabled={busy}
            onClick={() => onPickEmoji(option)}
            aria-label={`Avatar ${option}`}
            aria-pressed={value === option}
            className={`flex aspect-square items-center justify-center rounded-lg border text-xl transition-colors disabled:opacity-50 ${
              value === option
                ? 'border-accent bg-accent/15'
                : 'border-border hover:border-border-strong'
            }`}
          >
            {option}
          </button>
        ))}
      </div>

    </div>
  );
}
